import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';

import { Button } from '@/components/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/card.js';
import { Input } from '@/components/input.js';
import { LanguageSwitcher } from '@/components/language-switcher.js';
import { Label } from '@/components/label.js';
import { useToast } from '@/components/toast.js';
import { ApiError, api } from '@/lib/api.js';
import { useAuth } from '@/lib/auth-context.js';
import { useTranslation } from 'react-i18next';

export function LoginPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If the user is already signed in, send them straight to the dashboard.
  useEffect(() => {
    if (auth.user) void navigate({ to: '/' });
  }, [auth.user, navigate]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.auth.login(email, password);
      await auth.refresh();
      toast({ title: t('auth.welcomeBack'), variant: 'success' });
      void navigate({ to: '/' });
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-4">
      <div className="absolute right-4 top-4"><LanguageSwitcher /></div>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-indigo-400 shadow-glow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-primary-foreground">
              <path d="M5 5h14M5 10h9M5 15h11M5 20h7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              <path d="M18.5 13.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1z" fill="currentColor" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t('auth.productName')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('auth.signInToContinue')}</p>
        </div>
        <Card className="shadow-lift">
        <CardHeader>
          <CardTitle className="text-center text-lg">{t('auth.welcomeBack')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </form>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
